import {
  Alert,
  Container,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { CashManagementHeaderBreadcrumbs } from './CashOverviewPage';
import type { FC } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addMissingFactorsAndSort,
  useCashListConfiguration,
  useCashTransactionGroupPhoenix as useCashTransactionGroup,
  useCashTransactionGroupAddTransaction,
} from '@pflegenavi/frontend/api-nursing-home';
import type {
  Coin,
  GetCashTransactionGroupResultDto,
} from '@pflegenavi/shared/api';
import type { TransformedCashTransactionGroup } from '@pflegenavi/frontend/api-nursing-home';
import {
  CashManagementTransactionType,
  CashTransactionGroupType,
  CashListStorageType,
  FeatureFlag,
} from '@pflegenavi/shared/api';
import { Button, Iconify, Modal, ModalInner } from '@pflegenavi/web-components';
import { ChangeCashMode } from './components/form/model';
import { ChangeCashForm } from './components/form/ChangeCashForm';
import { UNRESTRICTED_CASH_LIST_RULES_BY_MODE } from './changeCashSelectorRules';
import { ReceiptTable } from './components/groupDetail/ReceiptTable';
import { GroupOverview } from './components/groupDetail/GroupOverview';
import { CashTransactionCard } from './components/groupDetail/CashTransactionCard';
import { TransactionsTable } from './components/groupDetail/TransactionsTable';
import { AssignToExpenseManagementModal } from './components/expenseManagement/AssignToExpenseManagementModal';
import { isAssignToExpenseEligible } from './components/expenseManagement/eligibility';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import {
  useEmployeeIsNursingHomeAdminOrManager,
  useNursingHomeContext,
} from '@pflegenavi/frontend/nursing-home-context';

interface AddCashTransactionModalProps {
  open: boolean;
  onClose: () => void;
  type: ChangeCashMode;

  onAddCashTransactionGroup: (
    type: ChangeCashMode,
    coins: Coin[],
    bankAccountAmount?: number,
    notes?: string
  ) => void;
  cashListId: string;
}

export const AddCashTransactionModal: FC<AddCashTransactionModalProps> = ({
  open,
  onClose,
  type,
  onAddCashTransactionGroup,
  cashListId,
}) => {
  const { selectedNursingHome } = useNursingHomeContext();

  return (
    <Modal open={open} handleClose={onClose}>
      <ModalInner>
        <ChangeCashForm
          forceCashListId={cashListId}
          selectorRulesByMode={UNRESTRICTED_CASH_LIST_RULES_BY_MODE}
          requireNote={false}
          handleClose={onClose}
          accountingPaymentsFeatureFlag={true}
          initialValues={{
            mode: type,
            step: 1,
          }}
          onConfirm={(data) =>
            Promise.resolve(
              onAddCashTransactionGroup(
                type,
                data.coins,
                data.bankAccountAmount,
                data.notes
              )
            )
          }
          mandatoryCashLink={
            selectedNursingHome?.legalEntity
              ?.mandatory_cash_link_for_receipts ?? false
          }
        />
      </ModalInner>
    </Modal>
  );
};

const CashTransactionGroupDetailPage: FC = () => {
  const { t } = useTranslation();

  return (
    <Container maxWidth={'lg'}>
      <CashManagementHeaderBreadcrumbs
        pageTitle={t('nav.cash-management.overview')}
        hideActionButton={true}
      />

      <CashTransactionGroupDetailContent />
    </Container>
  );
};

const CashTransactionGroupDetailContent: FC = () => {
  const { groupId } = useParams();
  const { t } = useTranslation();

  const { data, isLoading } = useCashTransactionGroup(groupId);

  const cashLists = useCashListConfiguration();
  const cashList = cashLists.cashLists.find(
    ({ id }) => id === data?.cashListId
  );
  const isCashStorageType =
    cashList?.storageType === CashListStorageType.Cash ||
    cashList?.storageType === CashListStorageType.CashOrBankAccount;

  const numCashTransactions = data?.cashListTransactions?.length ?? 0;
  const employeeIsNursingHomeAdminOrManager =
    useEmployeeIsNursingHomeAdminOrManager();

  if (isLoading || !data) {
    return (
      <Stack direction="column" gap={3} width="fit-content">
        <Skeleton variant="rectangular" height={400} width="100%" />
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap={3}>
      <Stack gap={3}>
        <Stack gap={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="h6" fontWeight="bold">
              {t('cashManagement.group-detail.overview.title')}
            </Typography>
            <GroupActionButtons group={data} />
          </Stack>

          <GroupOverview
            type={data.type}
            lastUpdateDate={data.updateDate}
            cashAmount={data.cashChanged ?? 0}
            separateFeeAmount={data.separateFeeAmount ?? 0}
            immediateFeeAmount={data.immediateFeeAmount ?? 0}
            cashTransactionCount={data.cashListTransactions?.length ?? 0}
            linkedTransactionCount={data.transactionLinkCount ?? 0}
            linkedReceiptCount={data.receiptLinkCount ?? 0}
            linkedAmount={data.amountLinked}
            linkedSeparateFeeAmount={data.separateFeeAmountLinked}
            linkedImmediateFeeAmount={data.immediateFeeAmountLinked}
            linkedCashListTransactionCount={data.cashListTransactionCount ?? 0}
            linkedPaymentIntentCount={data.paymentIntentLinkCount ?? 0}
            linkedPayoutCount={data.payoutLinkCount ?? 0}
            expensePartCount={data.expensePartCount}
          />
          {data.expensePartCount > 0 && (
            <Alert
              icon={
                <Iconify icon="tabler:receipt-euro" width={20} height={20} />
              }
              severity="success"
              variant="outlined"
              sx={{ alignItems: 'center' }}
            >
              {t('cashManagement.group-detail.expense-banner')}
            </Alert>
          )}
        </Stack>
        {numCashTransactions > 0 && (
          <Stack gap={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
            >
              <Stack gap={0.5}>
                <Typography variant="h6" fontWeight="bold">
                  {t(
                    'cashManagement.group-detail.overview.cash-transaction-title'
                  )}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('cashManagement.link.table-row.transfer-count', {
                    count: numCashTransactions,
                  })}
                </Typography>
              </Stack>
              {employeeIsNursingHomeAdminOrManager && (
                <WithdrawalDepositButtons group={data} />
              )}
            </Stack>

            <Grid container spacing={2}>
              {data.cashListTransactions?.map((transfer) => (
                <Grid
                  key={transfer.id}
                  size={{
                    xs: 12,
                    sm: 6,
                    lg: 4,
                  }}
                >
                  <CashTransactionCard
                    showDownloadPdf={
                      data.type ===
                        CashTransactionGroupType.ReceiptWithdrawal &&
                      transfer.type ===
                        CashManagementTransactionType.Withdrawal &&
                      isCashStorageType
                    }
                    {...transfer}
                  />
                </Grid>
              ))}
            </Grid>
          </Stack>
        )}
        <ReceiptTable groupId={groupId} />
        <TransactionsTable transactions={data.transactions ?? []} />
      </Stack>
    </Stack>
  );
};

export default CashTransactionGroupDetailPage;

const WithdrawalDepositButtons: FC<{
  group?: GetCashTransactionGroupResultDto;
}> = ({ group }) => {
  const { t } = useTranslation();

  const { selectedNursingHome } = useNursingHomeContext();

  const { mutateAsync: addTransaction } = useCashTransactionGroupAddTransaction(
    selectedNursingHome?.id
  );

  const [cashLinkOpen, setCashLinkOpen] = useState<undefined | ChangeCashMode>(
    undefined
  );

  if (
    !group ||
    group.type === CashTransactionGroupType.Transfer ||
    group.type === CashTransactionGroupType.Adjustment
  ) {
    return null;
  }

  const buttonDisabled =
    !group.manualTransactionCreationAllowed ||
    group.reversed ||
    group.type === CashTransactionGroupType.ResidentDeposit ||
    group.cashListTransactions?.some(({ fullReversal }) => fullReversal);
  return (
    <>
      <Stack direction="row" gap={1} justifyContent="flex-end">
        <Button
          variant="text"
          disabled={buttonDisabled}
          onClick={() => setCashLinkOpen(ChangeCashMode.Withdraw)}
          sx={{ width: 'fit-content' }}
          startIcon={<Iconify icon="eva:plus-fill" />}
        >
          {t('cashManagement.link.add-withdrawal')}
        </Button>
        <Button
          variant="text"
          disabled={buttonDisabled}
          onClick={() => setCashLinkOpen(ChangeCashMode.Deposit)}
          sx={{ width: 'fit-content' }}
          startIcon={<Iconify icon="eva:plus-fill" />}
        >
          {t('cashManagement.link.add-deposit')}
        </Button>
      </Stack>

      <AddCashTransactionModal
        key={(cashLinkOpen ?? 'closed').toString()}
        cashListId={group.cashListId}
        open={Boolean(cashLinkOpen)}
        onClose={() => setCashLinkOpen(undefined)}
        type={
          cashLinkOpen === 'withdraw'
            ? ChangeCashMode.Withdraw
            : ChangeCashMode.Deposit
        }
        onAddCashTransactionGroup={(type, coins, bankAccountAmount, notes) => {
          const coinsSorted =
            coins !== undefined && coins.length > 0
              ? addMissingFactorsAndSort(coins).map((coin) => coin.amount)
              : undefined;
          addTransaction({
            cashTransactionGroupId: group.id,
            type: type === ChangeCashMode.Withdraw ? 'withdrawal' : 'deposit',
            coins: coinsSorted,
            bankAccountAmount: bankAccountAmount,
            notes,
          });
        }}
      />
    </>
  );
};

const GroupActionButtons: FC<{
  group: TransformedCashTransactionGroup;
}> = ({ group }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const expenseManagementEnabled = useFeatureFlagEnabled(
    FeatureFlag.ExpenseManagement
  );
  const receiptManagementEnabled = useFeatureFlagEnabled(
    FeatureFlag.ReceiptManagement
  );

  const [assignOpen, setAssignOpen] = useState(false);

  const expenseExists = (group.expensePartCount ?? 0) > 0;

  // Assign-to-expense is shown only when the group is fully eligible (withdrawal
  // group, has an amount, nothing linked, no expense yet); otherwise hidden.
  const assignEligible = isAssignToExpenseEligible({
    type: group.type,
    amountLinkedCents: group.amountLinked,
    cashChangedCents: group.cashChanged,
    expensePartCount: group.expensePartCount,
  });

  // Open amount still to be linked. The receipt form expects positive cents,
  // so prefill with absolute values (deposit groups have positive amounts).
  const openAmount = group.cashChanged - group.amountLinked;
  // Receipt buttons are hidden once an expense exists; while shown, hint when
  // the group is already linked to a matching amount (no open amount left).
  const receiptTooltip =
    openAmount === 0
      ? t('cashManagement.group-detail.actions.already-matched-tooltip')
      : '';

  const handleCreateReceipt = (batch: boolean) => {
    const queryParams = new URLSearchParams();
    queryParams.set('cashTransactionGroupId', group.id);
    queryParams.set('receiptAmount', Math.abs(openAmount).toString());
    queryParams.set(
      'additionalFeeAmount',
      Math.abs(
        group.separateFeeAmount - group.separateFeeAmountLinked
      ).toString()
    );
    queryParams.set(
      'includedFeeAmount',
      Math.abs(
        group.immediateFeeAmount - group.immediateFeeAmountLinked
      ).toString()
    );
    queryParams.set('date', new Date(group.updateDate).toISOString());
    if (batch) {
      queryParams.set('cashListId', group.cashListId);
      navigate(
        `${nhAppMainPages.RECEIPTS_BATCH_ADD}?${queryParams.toString()}`
      );
    } else {
      navigate(
        `${
          nhAppMainPages.TRANSACTIONS_ADD_EMPTY_RESIDENT
        }?${queryParams.toString()}`
      );
    }
  };

  const showAssign = expenseManagementEnabled && assignEligible;
  const showReceiptButtons = receiptManagementEnabled && !expenseExists;
  if (!showAssign && !showReceiptButtons) {
    return null;
  }

  return (
    <>
      <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
        {showAssign && (
          <Button
            variant="contained"
            onClick={() => setAssignOpen(true)}
            startIcon={<Iconify icon="tabler:receipt-euro" />}
          >
            {t('cashManagement.actions.assign-to-expense')}
          </Button>
        )}
        {showReceiptButtons && (
          <>
            <Tooltip title={receiptTooltip} arrow>
              <span>
                <Button
                  variant="outlined"
                  disabled={openAmount === 0}
                  onClick={() => handleCreateReceipt(false)}
                  startIcon={<Iconify icon="eva:plus-fill" />}
                >
                  {t('receipts.unassociatedPliant.addReceipt')}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={receiptTooltip} arrow>
              <span>
                <Button
                  variant="outlined"
                  disabled={openAmount === 0}
                  onClick={() => handleCreateReceipt(true)}
                  startIcon={<Iconify icon="eva:plus-fill" />}
                >
                  {t('receipts.unassociatedPliant.addReceiptBatch')}
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </Stack>
      {assignOpen && (
        <AssignToExpenseManagementModal
          open
          onClose={() => setAssignOpen(false)}
          cashTransactionGroupId={group.id}
          groupAmountCents={Math.abs(group.cashChanged ?? 0)}
          pliantTransactionCreatedAt={group.pliantTransactionCreatedAt?.toISOString()}
        />
      )}
    </>
  );
};
